import { useTheme } from '@emotion/react';
import { useDispatch } from 'react-redux';

import { get } from 'lodash';
import PropTypes from 'prop-types';

import { Box } from '@mui/material';

import AsyncTableContainer from '../../Table/AsyncTableContainer';

import ExpandableImageComponent from '../../Image/ExpandableImageComponent';

import { deleteEvent } from '../actions';

import { avatarPlaceholderSRC } from '../../../utils';
import {
  checkCanUserEditEvent,
  formatDateTime,
  formatDateTimeRange,
  parseCategoriesList,
} from '../utils';

import { TABLE_CONSTANTS, TABLE_ID } from '../../Table/constants';

import { DATE_RANGES_TYPES, TABLE_DATA_TYPES } from '../../../staticData';
import { history } from '../../../store';
import ListActions from '../../Table/ListActions';
import {
  buildingFilter,
  eventStatusFilter,
  eventsMultiCategoryFilter,
  limitFilter,
} from '../../Table/filters';

const EventsDetailsListComponent = ({
  listParams,
  items,
  getItems,
  onLoad,
  listKey,
  buildings,
  currentUserId,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const columns = [
    {
      Header: 'Event',
      id: 'name',
      sortable: false,
      width: 200,
      accessor: ({ name, photo }) => (
        <Box display="flex" alignItems="center">
          <Box mr="1rem">
            <ExpandableImageComponent
              src={avatarPlaceholderSRC}
              photo={photo}
              caption={name}
              height="3.2rem"
              isSquare
            />
          </Box>
          <Box className="crop-long-text">{name}</Box>
        </Box>
      ),
    },
    {
      Header: 'Event time',
      id: 'starts_at',
      sortable: true,
      headerClassName: 'sort',
      width: 200,
      accessor: ({ startsAt, duration }) =>
        duration
          ? formatDateTimeRange(startsAt, duration)
          : formatDateTime(startsAt),
    },
    {
      Header: 'Organizer',
      id: 'organizer',
      sortable: false,
      accessor: ({ organizer, creator }) => organizer || creator?.fullName,
    },
    {
      Header: 'Description',
      id: 'description',
      sortable: false,
      accessor: ({ description }) => description,
    },
    {
      Header: 'Category',
      id: 'Category',
      sortable: false,
      accessor: ({ categories }) => parseCategoriesList(categories),
    },
    {
      Header: 'Location',
      id: 'location',
      sortable: false,
      accessor: ({ amenity, locationDescription }) =>
        locationDescription || amenity?.name,
    },
    {
      Header: 'RSVP`s',
      id: 'rsvp',
      sortable: false,
      width: 100,
      accessor: ({ attendeesCount }) => attendeesCount,
    },
    {
      Header: 'Action',
      id: 'action',
      sortable: false,
      width: 120,
      accessor: ({ id, creator, startsAt, duration }) => {
        const isEventEditable = checkCanUserEditEvent(
          currentUserId,
          creator?.id,
          startsAt,
          duration,
        );

        const actions = [
          {
            label: 'View details',
            color: theme.palette.primary.main,
            onClick: () => history.push(`/events/details/${id}/lists`),
          },
          {
            label: 'Edit Group',
            color: theme.palette.primary.main,
            onClick: () => history.push(`/events/${id}`),
            show: isEventEditable,
          },
          {
            label: 'Remove',
            color: theme.palette.secondary.main,
            onClick: () => deleteEvent(dispatch, id, listParams),
          },
        ];

        return (
          <ListActions
            color={theme.palette.primary.main}
            actions={actions}
            isFullWidth
          />
        );
      },
    },
  ];
  const filters = [
    buildingFilter(listParams, buildings, { unit: null }, false, 'building_id'),
    eventsMultiCategoryFilter(listParams),
    limitFilter(listParams),
    eventStatusFilter(listParams),
  ];

  const defaultParams = {
    ordering: '-starts_at',
    limit: 25,
    offset: 0,
    building_id: null,
    upcoming: 1,
  };

  const action = [
    {
      label: 'Create Event',
      url: '/events/add',
    },
  ];

  const onRowClick = (rowInfo) => {
    const id = get(rowInfo, 'original.id', null);
    id && history.push(`/events/details/${id}/lists`);
  };

  const rowStyles = {
    style: {
      cursor: 'pointer',
    },
  };

  const tableDataType = TABLE_DATA_TYPES.events;

  return (
    <AsyncTableContainer
      tableId={TABLE_ID.eventsDetailsTable}
      title="Events"
      columns={columns}
      defaultSort={TABLE_CONSTANTS.starts_at}
      defaultSortDesc
      manual
      filters={filters}
      showFiltersAreApplied={false}
      onLoad={onLoad}
      items={items}
      getItems={getItems}
      listParams={listParams}
      defaultParams={defaultParams}
      tableDataType={tableDataType}
      listKey={listKey}
      onRowClick={onRowClick}
      rowStyles={rowStyles}
      action={<ListActions actions={action} />}
      dateRangeType={DATE_RANGES_TYPES.starts}
      datePicker
      searchAutoFocus
    />
  );
};

EventsDetailsListComponent.propTypes = {
  listParams: PropTypes.instanceOf(Object),
  items: PropTypes.instanceOf(Object),
  getItems: PropTypes.func.isRequired,
  onLoad: PropTypes.func.isRequired,
  listKey: PropTypes.string.isRequired,
  buildings: PropTypes.instanceOf(Object),
  currentUserId: PropTypes.number,
};

export default EventsDetailsListComponent;
